/**
 * @module app.cm
 */

import { autoinject, observable, Disposable, ICollectionObserverSplice, BindingEngine } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { CarePlanGoalWithLocalState } from 'app/cm/models/local-state-extensions';
import { getReference } from 'app/shared/models/essette-types';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/api/api';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { ApiDate } from 'app/shared/models/essette-api';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as CM from 'app/cm/models/essette-api';
import { guid } from 'app/shared/lib/guid';

export type HasInterventionsFlag = { hasInterventions: boolean };
export type GoalWithLocalStateAndInterventionFlag = CarePlanGoalWithLocalState & HasInterventionsFlag;

@autoinject()
export class AddGoals extends ComponentBase implements IForm {


    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    public caseId: string | null = null;

    @bindable.boolean
    public showActions: boolean = false;

    /** the value of the autocomplete textbox */
    @observable
    public goalSearchQuery: string | null = null;

    protected goalSearchResults = new Array<CM.CarePlanGoalDefinition>();     // array of search results from API for autocomplete (not currently used)
    protected currentGoals: CarePlanGoalWithLocalState[] = [];
    protected selectedGoals: CM.CarePlanGoalDefinition[] = [];
    protected goalLookups: CM.CarePlanGoalDefinition[] = [];
    protected priorityLookups: CM.CarePlanGoalPriority[] = [];
    @bindable.string
    protected goalOption: string = 'selectGoals';
    protected previousGoalOption: string = 'selectGoals';
    protected selectedGoalsSubscription: Disposable | null = null;
    protected insertGoals: GoalWithLocalStateAndInterventionFlag[] = [];
    protected customGoalText: string | null = null;
    protected allowDuplicateGoals: boolean = false;

    /** Permission that controls whether a user can enter a custom goal */
    protected hasAddCustomGoals: boolean = false;
    /** Permission that controls whether a custom goal can be added to the library */
    protected hasAddCustomGoalToLibrary: boolean = false;

    public step: number = 1;

    protected maxCustomDescriptionLength = 200;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly carePlanService: CarePlanService,
        private readonly bindingEngine: BindingEngine,
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.caseId) { throw new Error('Bindable property [case-id] was not set'); }

        this.checkPermissions();
        this.loadCurrentGoals();
        this.loadGoalDefinitions();
        this.loadPriorities();
    }

    protected attached(): void {
        this.observeSelectedTasks();
    }

    protected detached(): void {
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected selectedGoalsChanged(splices: ICollectionObserverSplice<CM.CarePlanGoalDefinition>[]): void {
        if(this.insertGoals.length === 0 && this.selectedGoals.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var goalsAdded = this.selectedGoals.slice(splice.index, splice.index + splice.addedCount);
            if(goalsAdded.length > 0) {
                goalsAdded.forEach(
                    goal => {
                        const g: GoalWithLocalStateAndInterventionFlag = {
                            isCustom: false,
                            description: goal.description,
                            definition: { id: goal.id },
                            case: { id: this.caseId },
                            _state: { isSaved: false, addToLibrary: false },
                            hasInterventions: false
                        };
                        this.insertGoals.push(g);
                    });
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanGoalDefinition[]).forEach(
                    goal => {
                        let i = this.insertGoals.find(g => (g.definition && g.definition.id) === goal.id);
                        if(i) {
                            this.insertGoals.splice(this.insertGoals.indexOf(i), 1);
                        }
                    });
            }
        }
        if(this.insertGoals.length === 0) this.$autocomplete?.clear();
    }

    protected optionsChoiceChanged(): void {
        this.previousGoalOption = this.goalOption;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.allowDuplicateGoals = !!getApiSetting(this.context.settings, 'carePlan.goals.$allowDuplicates');
        this.hasAddCustomGoals = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.AddCustom');
        this.hasAddCustomGoalToLibrary = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.AddToLibrary');
    }

    protected observeSelectedTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
        this.selectedGoalsSubscription = this.bindingEngine
            .collectionObserver(
                this.selectedGoals) // observe the selectedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(
                this.selectedGoalsChanged
                    .bind(this)); // pass the splice information (addedCount, index, removed) to selectedTasksChanged method

        // subscription disposed of in detached()
    }

    protected addAutoCompleteGoal(goal: CM.CarePlanGoalDefinition): void {

        this.selectedGoals.push(goal); // will trigger selectedTasksChanged(), and add the task to selectedTasks
        //this.taskSearchInput.value = '';      // clear the search input
        this.goalSearchQuery = '';

        this.goalSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    /** Called when a user types in a custom goal description and clicks the button */
    protected addCustomGoal(): void {
        const goal: GoalWithLocalStateAndInterventionFlag = {
            isCustom: true,
            description: this.customGoalText,
            case: { id: this.caseId },
            _state: { isSaved: false, addToLibrary: false },
            hasInterventions: false
        };

        this.insertGoals.push(goal);

        this.customGoalText = null;
    }

    /** Called when a user removes a goal from the grid. Will ultimately trigger the selectedGoalsChanged() method */
    protected removeGoal(goal: GoalWithLocalStateAndInterventionFlag): void {
        if(goal.isCustom) {
            // Custom tasks are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertGoals.indexOf(goal);
            if(index >= 0) {
                this.insertGoals.splice(index, 1);
            }
        }
        else {
            // Find the task in the selectedTasks array and remove it. This will trigger the selectedTasksChanged(), which will update insertTasks
            const st = this.selectedGoals.find(t => t.id === (goal.definition && goal.definition.id));
            if(st) {
                this.selectedGoals.splice(this.selectedGoals.indexOf(st), 1);
            }
        }
    }

    protected isDuplicate(goal: CM.CarePlanGoal): boolean {
        return this.currentGoals.some(g => (goal.definition && goal.definition.id) === (g.definition && g.definition.id));
    }

    protected filterGoals = (goal: CM.CarePlanGoalDefinition): boolean => {
        if(this.selectedGoals.some(g => goal.id === g.id) && this.goalOption === 'searchGoals') {
            return false;
        }
        if(this.allowDuplicateGoals) { return true; }
        if(!this.currentGoals && !goal) { return false; }
        return !this.currentGoals.some(g => goal.id === (g.definition && g.definition.id));
    }

    protected copyPriority(priority: CM.CarePlanGoalPriority | null): void {
        this.insertGoals.forEach(g => {
            g.priority = priority;
        });
    }

    protected copyTargetDate(date: ApiDate | null): void {
        this.insertGoals.forEach(g => {
            g.targetDate = date;
        });
    }

    /** Takes a goal from the insertGoals grid and constructs a new copy to be sent to the API */
    protected buildSaveGoal(goal: CarePlanGoalWithLocalState): CarePlanGoalWithLocalState {
        if(!goal._state) { goal._state = {}; }

        let saveGoal: CarePlanGoalWithLocalState = {
            case: { id: this.caseId },
            isCustom: goal.isCustom,
            priority: getReference(goal.priority) || undefined,
            targetDate: goal.targetDate,
            definition: (!goal.isCustom && getReference(goal.definition)) || undefined,
            description: (!goal.definition && goal.description) || undefined
        };

        if(this.hasAddCustomGoalToLibrary && !!goal._state.addToLibrary) {
            saveGoal.isCustom = false;
            delete saveGoal.definition;
        }

        return saveGoal;
    }

    protected addCaseProperty(model: CM.CaseTask): void {
        model.case = { id: this.caseId };
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveSelectedGoals();
    }

    public reset(): void {
        this.selectedGoals.splice(0);
        this.insertGoals.splice(0);
        this.goalSearchQuery = '';
        this.goalSearchResults.splice(0);
        this.goalOption = 'selectGoals';
        this.step = 1;
        this.$autocomplete?.clear();
    }

    ////****************** Service Calls ********************************************////
    /** Iterates through the goals and saves each one. */
    @apiCall
    private async saveSelectedGoals(): Promise<boolean> {
        const duplicateGoals: string[] = [];

        const goals = this.insertGoals
            .filter(g => {
                // If user clicks save a second time, we skip goals that might have been saved already.
                return !(g._state && g._state.isSaved);
            })
            .map(g => {
                if(this.allowDuplicateGoals && this.currentGoals.some(cg => (cg.definition && cg.definition.id) === (g.definition && g.definition.id))) {
                    duplicateGoals.push(g.description || '');
                }

                const goal = this.buildSaveGoal(g);

                return goal;
            });

        /** Show confirmation to add duplicate goals */
        if(duplicateGoals.length > 0) {
            const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateGoals.length, what: '$t(cm:carePlanGoal.$)' });
            const result = await confirmation.showCustom(message, duplicateGoals.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const promises = new Array<Promise<IApiActionResults>>();
            let customGoalAddedToLibrary = false;
            for(const g of goals) {
                promises.push(this.carePlanService.insertCarePlanGoal(g));
                if(!g.definition?.id) customGoalAddedToLibrary = true;
            }

            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => (r.isSuccessful));
            if(customGoalAddedToLibrary) this.loadGoalDefinitions();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }

    }

    @apiCall
    public async loadCurrentGoals(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes = [
            'definition'
        ];

        const fields = [
            'definition.id'
        ];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadCaseCarePlan(this.caseId, options, includes, fields);
            this.currentGoals = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete task search box */
    @apiCall
    protected async searchGoals(value: string): Promise<void> {
        if(!value) {
            if(this.goalSearchResults) this.goalSearchResults = [];
            return;
        }

        const fields = [
            'description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.searchGoalDefinitions(value, undefined, undefined, false, fields);
            this.goalSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadGoalDefinitions(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = [
            'description',
            'isCustom'
        ];

        try {
            this.state.beginLoad();

            const response = await this.carePlanService.loadGoalDefinitions(options, undefined, false, fields);
            this.goalLookups = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadPriorities(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();

            const response = await this.carePlanService.loadCarePlanGoalPriorities(options, undefined, fields);
            this.priorityLookups = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
